import { useRef, useState, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { Helmet } from 'react-helmet-async'
import { useParams } from 'react-router-dom'
import { startOfWeek } from 'date-fns'

// Components
import { MainBackground } from '../components/timeline/MainBackground'
import { Weeks } from '../components/timeline/weeks'
import { DateSwitcher } from '../components/timeline/DateSwitcher'
import { HourRows } from '../components/timeline/HourRows'
import { TimelineTopBar } from '../components/timeline/TimelineTopBar'
import { TimelinePopup } from '../components/timeline/TimelinePopup'
import { LessonDetailView } from '../components/lessons/LessonDetailView'
import { VerificationBanner } from '../components/verification/VerificationBanner';


// Styles & Constants
import { NAVIGATION_HEIGHT } from '../constants/global'
import { titles } from '../constants/titles'
import { Area, BaseViewPort, Corner, Scrollbar, Thumb } from '../styles/_app.style'
import { DesktopTimelineContainer } from '../styles/timeline/desktopTimeline.style'
import { Dialog, Overlay } from '../styles/timeline/popup.styles'
import {
  BottomGapStrip,
  BottomOfMainStrip,
  BottomOfMainStripHelper,
  BottomOfModuleStrip,
  ExperimentArea,
  GapCover,
  GapCover2,
  TopOfModuleStrip,
} from '../styles/timeline/gridAreas'

// Contexts & Hooks
import { useTimelineDateValue, useTimelineDateSetter, useTimelineDate } from '../contextManager/timelineDate.context'
import { useUser } from '../contextManager/user.context'
import { useEvents } from '../hooks/useEvents'
import { useScale } from './HeightScaler'
import { useScrollToNow } from '../hooks/useScrollToNow'
import { useEventModal } from '../hooks/useEventModal'
import { useCellSelection } from '../hooks/useCellSelection'
import { getTimezoneOffset, adjustAvailabilityForTimezone } from '../utils/availability'
import { getDetectedTimezone } from '../utils/timezones'

const TOP_MARGIN = `(${NAVIGATION_HEIGHT})`

const Timeline = () => {
  // --- 1. CONFIG & DATA ---
  const { scale } = useScale()
  const { year } = useParams<{ year: string }>()
  const selectedYear = year ? +year : new Date().getFullYear()
  
  const { userDetails } = useUser()
  const userId = userDetails?.id
  const { events } = useEvents()

  // Timezone toggle
  const [isViewingBrowserTz, setIsViewingBrowserTz] = useState(false)
  const homeTz = userDetails?.timezone
  const browserTz = useMemo(getDetectedTimezone, [])
  const tzMismatch = !!(homeTz && browserTz && homeTz !== browserTz)
  const { setViewingTz } = useTimelineDate()

  // Keep the shared context in sync with the local toggle
  const handleToggleTz = () => {
    setIsViewingBrowserTz(prev => {
      const next = !prev;
      setViewingTz(next ? browserTz : null);
      return next;
    });
  }

  // Availability is always stored in home tz. The grid handles tz display via activeTz + fractionalHour.
  const displayAvailability = userDetails?.availability ?? null;

  const selectedDate = useTimelineDateValue()
  const setSelectedDate = useTimelineDateSetter()
  const weekStart = startOfWeek(selectedDate, { weekStartsOn: 1 })

  // --- 2. LOGIC HOOKS ---
  const viewportRef = useRef<HTMLDivElement>(null)
  
  // Logic 1: Auto-scroll on mount
  useScrollToNow(viewportRef)

  // Logic 2: Event Details Modal
  const {
    isOpen: isLessonOpen,
    event: lessonEvent,
    avatar: lessonAvatar,
    openById: openLesson,
    close: closeLesson,
  } = useEventModal(events, userDetails ?? null)

  // Logic 3: Empty Cell Selection (Popup)
  const {
    selectedCell,
    isKeyboardOpen,
    select: selectCell,
    close: closeCell,
    handleFocus,
    handleBlur,
  } = useCellSelection(viewportRef)

  // --- 3. VIEW HELPERS ---
  const activeClasses = `
    ${selectedCell ? 'popup-open' : ''} 
    ${isKeyboardOpen ? 'keyboard-open' : ''}
  `

  return (
    <>
      <Helmet>
        <title>{titles.timeline}</title>
      </Helmet>

      {/* <VerificationBanner /> */}
      <GapCover />
      <GapCover2 />
      
      <Area
        style={{ '--scale': scale } as React.CSSProperties}
        css={{
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          height: `calc((100dvh / ${scale}) - ${TOP_MARGIN})`,
          marginTop: `calc(${TOP_MARGIN})`,
        }}
      >
          <BaseViewPort ref={viewportRef} css={{ overscrollBehavior: 'none' }}>          <DesktopTimelineContainer className={activeClasses}>
            <TimelineTopBar
              homeTz={homeTz}
              browserTz={browserTz}
              isViewingBrowserTz={isViewingBrowserTz}
              onToggleTz={handleToggleTz}
            />

            <MainBackground
              timelineStart={weekStart}
              events={events}
              availability={displayAvailability}
              activeTz={isViewingBrowserTz ? browserTz : homeTz}
              homeTz={homeTz}

              // Handlers
              onCellClick={selectCell}
              onClose={closeCell}
              activeCell={selectedCell}
              onEventClick={openLesson}
            />

            <Weeks start={weekStart} />
            <HourRows activeTz={isViewingBrowserTz ? browserTz : homeTz} />
            <TopOfModuleStrip />
            
            <DateSwitcher
              selectedDate={selectedDate}
              onDateChange={setSelectedDate}
            />

            <BottomGapStrip className={activeClasses} />
            
            <ExperimentArea className={activeClasses}>
              {selectedCell && (
                <TimelinePopup
                  date={selectedCell.date}
                  anchorRect={selectedCell.rect}
                  onClose={closeCell}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  viewingTz={isViewingBrowserTz ? browserTz : homeTz}
                  homeTz={homeTz}
                />
              )}
            </ExperimentArea>
          </DesktopTimelineContainer>

          <BottomOfModuleStrip className={activeClasses} />
          <BottomOfMainStrip className={activeClasses} />
          <BottomOfMainStripHelper className={activeClasses} />
        </BaseViewPort>

        <Scrollbar orientation="vertical"><Thumb /></Scrollbar>
        <Scrollbar orientation="horizontal"><Thumb /></Scrollbar>
        <Corner />

        {/* --- MODAL RENDERING --- */}
        {isLessonOpen && lessonEvent && createPortal(
          <Overlay onClick={closeLesson} style={{ zIndex: 9999 }}>
            <Dialog
              onClick={(e) => e.stopPropagation()}
              style={{
                maxWidth: '900px',
                width: '90%',
                height: '90vh',
                overflow: 'hidden',
                padding: 0,
                background: 'transparent',
                boxShadow: 'none',
                display: 'flex',
              }}
            >
              <LessonDetailView
                event={lessonEvent}
                onClose={closeLesson}
                isTutor={userDetails?.role === 'tutor'}
                currentUserId={userId}
                avatar={lessonAvatar}
                initialTz={isViewingBrowserTz ? browserTz : undefined}
              />
            </Dialog>
          </Overlay>,
          document.body
        )}
      </Area>
    </>
  )
}

export default Timeline